/**
 * Ossature commune des ecrans de jeu : rail de scores a gauche, contenu a
 * droite.
 *
 * Les scores restent visibles en permanence, y compris pendant le dessin et la
 * devinette : c'est ce qui donne le sentiment de partie plutot que de suite de
 * manches isolees. Le rail est fixe, seul le contenu peut defiler.
 *
 * SUR ECRAN ETROIT, le rail devient un tiroir, FERME par defaut a toutes les
 * phases. Il y prenait 62 px de large en permanence, soit un cinquieme d'un
 * telephone, au moment precis ou l'on a besoin de surface pour jouer. Une
 * languette l'ouvre a la demande, et ce choix tient : rien ne le rouvre ni ne
 * le referme dans le dos du joueur au changement de phase. Ouvert, il passe
 * PAR-DESSUS le tableau : le canvas ne change jamais de taille, ce qui evite de
 * deplacer le dessin sous le doigt.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import { isElimination, type RoomSnapshot } from '@retusch/shared';
import { Scoreboard } from './Scoreboard.js';
import { GameBrand } from './GameBrand.js';
import { useT } from '../locales/useT.js';
import { useCompact } from './useCompact.js';
import { ScoresIcon } from './icons.js';

export function GameLayout({
  room,
  selfId,
  children,
}: {
  room: RoomSnapshot;
  selfId: string | null;
  children: ReactNode;
}): ReactElement {
  const t = useT();
  const compact = useCompact();
  const [open, setOpen] = useState(false);

  if (!compact) {
    return (
      <div className="game-layout">
        {/* Le nom du jeu coiffe le rail : c'est le coin haut gauche de l'ecran
            de jeu, sans rien recouvrir. Il vit HORS du rail defilant, sinon il
            disparaitrait des que la liste des joueurs deborde. */}
        <div className="game-layout__rail">
          <GameBrand />
          <Scoreboard
            players={room.players}
            selfId={selfId}
            elimination={isElimination(room.settings.mode)}
          />
        </div>
        <div className="game-layout__main">{children}</div>
      </div>
    );
  }

  return (
    <div className="game-layout game-layout--compact" data-scores={open}>
      <div className="game-layout__main">{children}</div>

      {open && (
        <Scoreboard
          players={room.players}
          selfId={selfId}
          elimination={isElimination(room.settings.mode)}
        />
      )}

      {/* Languette : toujours accessible, jamais devant le tableau. Elle porte
          le nombre de joueurs, qui est l'information qu'on vient chercher. */}
      <button
        type="button"
        className="scores-tab"
        aria-expanded={open}
        aria-label={t(open ? 'game.hideScores' : 'game.showScores')}
        onClick={() => setOpen((value) => !value)}
      >
        <ScoresIcon />
        <span className="scores-tab__count mono-tabular">{room.players.length}</span>
      </button>
    </div>
  );
}
